import { Injectable, inject } from "@angular/core";
import type { CreateEntityDto, DatabaseStore, EntityService, PersistentContainer } from "@newaydata/logbook-core";
import type { LogbookDb } from "@newaydata/logbook-schemas/generated/typescript/logbook";
import { Observable, Subject } from "rxjs";
import { legIssues } from "@newaydata/logbook-fleetplan";

import { environment } from "../../../environments/environment";
import { PLATFORM_ADAPTER } from "../../core/platform/platform.adapter";
import { generateUuid } from "../../core/uuid";
import { type ClientTokenClaims, ClientTokenService } from "../auth/client-token.service";
import { EnvironmentService } from "../env/env.service";
import type { BoardBook, BookHeader, LogEntry } from "./boardbook-mapping";
import {
  aircraftDto,
  bookView,
  boardBookDto,
  crewDto,
  emptyBoardBook,
  entryFromRows,
  headerFromRow,
  legDto,
  personDto,
  personFilter,
  placeDto,
} from "./boardbook-mapping";
import { applyEntryOp, applyHeaderOp, type EntryOp } from "./boardbook-fold";
import { fillSeats } from "./boardbook-model";
import {
  BoardbookKernelClient,
  boardbookSyncTarget,
  type RemoteRowOp,
  type SettledOp,
} from "./boardbook-kernel-client";
import {
  BOARDBOOK_DATASET,
  HEADER_UUID,
  LEGACY_BOOK_UUID,
  bodyToEntry,
  bodyToHeader,
  entryToBody,
  headerToBody,
  legacyBlobToEntries,
} from "./boardbook-sync-codec";
import { LOGBOOK_CONTAINER } from "./logbook-container";
import { LAST_STORE_KEY } from "../auth/binding-wipe";
import { BoardbookOutbox } from "./boardbook-outbox";

export type { BoardBook } from "./boardbook-mapping";
export { emptyBoardBook } from "./boardbook-mapping";

/** Thrown when a caller tries to change a sealed (exported) entry. */
export class LockedEntryError extends Error {
  constructor(id: string) {
    super(`logbook entry ${id} is locked`);
  }
}

/** Thrown when the kernel holds a newer body than the one exported; the local entry now holds it. */
export class SupersededExportError extends Error {
  constructor(
    id = "",
    public readonly sealed = false
  ) {
    super(`logbook entry ${id} was superseded on another device`);
  }
}

/**
 * Thrown when a caller deletes an exported entry that still waits for the Fleetplan sender
 * (`queued` or `syncing`), or whose Fleetplan state this device does not know yet (`unknown`).
 * A delete would take the leg out of the Fleetplan queue (OE-XNF, 2026-10-16).
 */
export class WaitingEntryError extends Error {
  constructor(
    id: string,
    public readonly unknown: boolean
  ) {
    super(`logbook entry ${id} waits for Fleetplan`);
  }
}

/** Thrown when an edit is saved over an entry that changed or was deleted since the form loaded it. */
export class StaleEntryError extends Error {
  constructor(id: string) {
    super(`logbook entry ${id} changed since it was loaded`);
  }
}

/** Whether a stream item belongs to the room of the store. */
function inRoom(item: { tenant: string; scope: string }, storeKey: string): boolean {
  return roomOf(storeKey) === `${item.tenant}:${item.scope}`;
}

/** `<tenant>:<registration>` part of a store key. */
function roomOf(storeKey: string): string {
  return storeKey.replace(/^boardbook:/, "");
}

/** One stamp per store and tail: a web login can switch tails, and a wiped store gets a new id. */
function headerStampKey(storeId: string, registration: string): string {
  return `efb.logbook.headerStamp.${storeId}.${registration}`;
}

@Injectable({
  providedIn: "root",
})
export class LogbookService {
  private buildContainer = inject(LOGBOOK_CONTAINER);
  private clientToken = inject(ClientTokenService);
  private env = inject(EnvironmentService);
  private kernel = inject(BoardbookKernelClient);
  private platform = inject(PLATFORM_ADAPTER);

  /** The opened logbook handle — built once, lazily, and reused. */
  private dbP: Promise<LogbookDb> | null = null;
  /** The container of that handle. A save runs in its transaction. */
  private container: PersistentContainer | null = null;
  /** Store name of the open handle. */
  private openKey = "";
  /** Claims of the running write unit, read once when the unit starts. */
  private unitClaims: ClientTokenClaims | null | undefined;

  /** Serializes ALL DB mutations (local edits + remote folds). `save()`/`clearLocal()`
   * rewrite-all the leg set, so two overlapping writers would corrupt each other. */
  private writeChain: Promise<unknown> = Promise.resolve();

  /** Id of the opened store, set by `openDb()`. Part of the header stamp key. */
  private storeId = "";

  /** LWW stamps of the book header, also kept in localStorage so an offline header
   * edit still wins over an older kernel header row after a restart. */
  private readonly headerStamps = new Map<string, number>();

  /** Emits the post-fold book after a remote op changed the local DB. Computed
   * inside the write lock, so the page binds it directly instead of doing its own
   * (unlocked) reload that could read a rewrite-all mid-flight. */
  private readonly changed$ = new Subject<BoardBook>();

  /** The kernel's lww/immutable/queued refusals of this device's own ops. */
  get refused$(): Observable<{ uuid: string; reason: string }> {
    return this.kernel.refused$;
  }

  /**
   * Fleetplan state per store and entry id, as the kernel last sent it. Memory only: the
   * client replays from serverSeq 0 at each start, and the replay carries the state.
   * ponytail: an offline start knows no state, so exported entries stay until the replay.
   */
  private readonly lifecycles = new Map<string, Map<string, string | null>>();

  private lifecycleMap(): Map<string, string | null> {
    const key = this.storeKey();
    let map = this.lifecycles.get(key);
    if (!map) this.lifecycles.set(key, (map = new Map()));
    return map;
  }

  /**
   * The Fleetplan state of an entry: `queued`, `syncing`, `synced`, `failed`, null (never
   * queued), `deleted` (the kernel row is gone, but this device keeps the sealed entry) or
   * undefined (not known on this device yet).
   */
  public lifecycleOf(id: string): string | null | undefined {
    return this.lifecycleMap().get(id);
  }

  /** Records the state a remote upsert carries. True when it changed. */
  private noteLifecycle(op: RemoteRowOp): boolean {
    if (op.type !== "upsert" || op.lifecycle === undefined) return false;
    const map = this.lifecycleMap();
    if (map.has(op.uuid) && map.get(op.uuid) === op.lifecycle) return false;
    map.set(op.uuid, op.lifecycle);
    return true;
  }

  constructor() {
    // Own the fold pipeline so peers' edits land in the DB even with the page
    // closed (offline/background sync). Each op is serialized against local writes.
    // A fold failure must be surfaced (logged), not silently swallowed by the
    // write-chain's continuity catch — else a peer's edit vanishes with no trace.
    this.kernel.remoteOps$.subscribe((op) =>
      this.serialize(() => (inRoom(op, this.storeKey()) ? this.handleRemoteOp(op) : undefined)).catch((err) =>
        console.error("logbook remote fold failed", err)
      )
    );
    this.kernel.synced$.subscribe((s) =>
      this.serialize(async () => this.drain(`${s.tenant}:${s.scope}`)).catch((err) =>
        console.error("logbook outbox drain failed", err)
      )
    );
    this.kernel.settled$.subscribe((s) => this.settle(s));
    // After a replay from sinceSeq 0, a row the kernel did not name is a row it never received.
    this.kernel.fullReplay$.subscribe((replay) =>
      this.serialize(() => (inRoom(replay, this.storeKey()) ? this.resendMissing(replay.uuids) : undefined)).catch(
        (err) => console.error("logbook resend failed", err)
      )
    );
  }

  private async resendMissing(named: ReadonlySet<string>): Promise<void> {
    const book = await this.load();
    for (const e of book.entries) {
      // A sealed body pushed without an export would seal the kernel row.
      if (!e.lockedAt && !named.has(e.id)) this.pushUpsert(e.id, e.updatedAt, entryToBody(e));
    }
    if (book.header.dateOpened && !named.has(HEADER_UUID)) {
      this.pushUpsert(HEADER_UUID, book.header.updatedAt, headerToBody(book.header));
    }
  }

  /** The running unit's claims, else the token's. */
  private claims(): ClientTokenClaims | null {
    return this.unitClaims !== undefined ? this.unitClaims : this.clientToken.getClaims();
  }

  /** Record the op in the outbox of the open store; hand it to the kernel only while the client is bound to that store's room. */
  private pushUpsert(uuid: string, updatedAt: number, body: unknown): void {
    this.outbox.record(this.openKey, uuid, { type: "upsert", updatedAt, body });
    this.ensureSync();
    if (this.boundToOpenStore()) this.kernel.pushUpsert(uuid, updatedAt, body);
  }

  private pushDelete(uuid: string, updatedAt: number): void {
    this.outbox.record(this.openKey, uuid, { type: "delete", updatedAt });
    this.ensureSync();
    if (this.boundToOpenStore()) this.kernel.pushDelete(uuid, updatedAt);
  }

  /** The client is bound to the open store's room. Otherwise the op waits in the outbox for that room's SYNCED. */
  private boundToOpenStore(): boolean {
    const room = roomOf(this.openKey);
    return room !== ":" && this.kernel.room === room;
  }

  private drain(room: string): void {
    const key = this.storeKey();
    // The client can have moved to another room while this drain waited in the write chain.
    if (roomOf(key) !== room || this.kernel.room !== room) return;
    for (const [uuid, op] of Object.entries(this.outbox.ops(key))) {
      if (op.type === "delete") this.kernel.pushDelete(uuid, op.updatedAt);
      else this.kernel.pushUpsert(uuid, op.updatedAt, op.body);
    }
  }

  private settle(s: SettledOp): void {
    if (s.outcome !== "retry") this.outbox.settle(`boardbook:${s.tenant}:${s.scope}`, s.uuid, s.updatedAt);
  }

  private readonly outbox = new BoardbookOutbox();

  /** The unit's aircraft is the live token's. No session on both sides counts as the same. */
  private sameRoom(): boolean {
    const unit = this.claims();
    const live = this.clientToken.getClaims();
    return unit?.tenant === live?.tenant && unit?.registrationId === live?.registrationId;
  }

  private lastStoreKey = "";

  /** Name of the store for the token's tenant and registration. Without a session, the last known one. */
  private storeKey(): string {
    const claims = this.claims();
    if (claims?.tenant && claims?.registrationId) {
      const key = `boardbook:${claims.tenant}:${claims.registrationId}`;
      if (key !== this.lastStoreKey) {
        this.lastStoreKey = key;
        try {
          localStorage.setItem(LAST_STORE_KEY, key);
        } catch {
          // No storage: the key lives in memory only.
        }
      }
      return key;
    }
    if (!this.lastStoreKey) {
      try {
        this.lastStoreKey = localStorage.getItem(LAST_STORE_KEY) ?? "";
      } catch {
        // No storage.
      }
    }
    return this.lastStoreKey || `boardbook:${claims?.tenant ?? ""}:${claims?.registrationId ?? ""}`;
  }

  private db(): Promise<LogbookDb> {
    // One store per tenant and registration: a changed key drops the memo, so the
    // next open switches the store. Callers run under the write lock.
    const key = this.storeKey();
    if (key !== this.openKey) this.dbP = null;
    // Reset the memo on failure so a transient open error (wasm fetch, OPFS,
    // migration) doesn't cache a rejected promise and brick the page for the
    // whole app lifetime — the next call retries.
    return (this.dbP ??= this.openDb(key).catch((e) => {
      this.dbP = null;
      throw e;
    }));
  }

  private async openDb(key: string): Promise<LogbookDb> {
    const container = this.container ?? (await this.buildContainer());
    const stores = await container.list();
    let store = stores.find((s) => s.name === key);
    if (!store) {
      // Adopt the old single store once, only when it holds this aircraft's book.
      const legacy = stores.find((s) => s.name === "boardbook");
      if (legacy && (await this.legacyHoldsRegistration(container, legacy))) {
        // Close first: closing the open legacy handle would else write its old name back.
        await container.close();
        store = await container.rename(legacy, key);
      }
    }
    store ??= await container.create(key);
    const db = await container.open<LogbookDb>(store);
    this.storeId = store.id;
    this.container = container;
    this.openKey = key;
    return db;
  }

  private async legacyHoldsRegistration(container: PersistentContainer, legacy: DatabaseStore): Promise<boolean> {
    const db = await container.open<LogbookDb>(legacy);
    const book = (await db.boardBooks.list())[0];
    if (!book) return true;
    const aircraft = book.aircraft ? await db.aircraft.get(book.aircraft) : undefined;
    return aircraft?.registration === this.claims()?.registrationId;
  }

  private readHeaderStamp(registration: string): number {
    const key = headerStampKey(this.storeId, registration);
    const cached = this.headerStamps.get(key);
    if (cached !== undefined) return cached;
    try {
      const stamp = Number(localStorage.getItem(key));
      return Number.isFinite(stamp) && stamp > 0 ? stamp : 0;
    } catch {
      return 0;
    }
  }

  private storeHeaderStamp(registration: string, stamp: number): void {
    const key = headerStampKey(this.storeId, registration);
    this.headerStamps.set(key, stamp);
    try {
      localStorage.setItem(key, String(stamp));
    } catch {
      // No storage: the stamp lives in memory, and after a restart the kernel's header row wins.
    }
  }

  private queuedWrites = 0;

  /** Units queued or running under the write lock. `save()` rewrites every leg and
   *  is not atomic, so a page reload must wait for this to reach 0. */
  public get writesInFlight(): number {
    return this.queuedWrites;
  }

  /** Board-book ops a page reload would lose. An expired token only delays them to
   *  the next ensureSync(), so they count; 0 when the selected backend has no kernel,
   *  where nothing will ever send them. */
  public get unsentSyncOps(): number {
    return this.kernelWsUrl() ? this.kernel.pendingCount : 0;
  }

  /** Run a DB-mutating unit under the write lock (FIFO, failure-isolated). */
  private serialize<T>(fn: () => Promise<T>): Promise<T> {
    this.queuedWrites++;
    // A token change while the unit waits on I/O must not make it read one aircraft's
    // book and write it into another's store, so the unit keeps the claims it started with.
    const claims = this.clientToken.getClaims();
    const run = () => {
      this.unitClaims = claims;
      return fn();
    };
    const next = this.writeChain.then(run, run).finally(() => this.queuedWrites--);
    this.writeChain = next.catch(() => undefined);
    return next;
  }

  public async load(): Promise<BoardBook> {
    const db = await this.db();
    const boardBook = (await db.boardBooks.list())[0];
    if (!boardBook) return emptyBoardBook();

    // Keep insertion order (db.legs.list is rowid-ordered): save() rewrite-alls
    // legs in entries order, so a reload matches the order shown right after a
    // commit, and the list stays stable across reloads.
    const legs = await db.legs.list({ board_book_id: boardBook.id });
    const aircraft = boardBook.aircraft ? await db.aircraft.get(boardBook.aircraft) : undefined;

    const entries: LogEntry[] = await Promise.all(
      legs.map(async (leg) => {
        const members = await db.crew.list({ leg_id: leg.id });
        const [departurePlace, arrivalPlace, crew] = await Promise.all([
          leg.departure_place ? db.places.get(leg.departure_place) : undefined,
          leg.arrival_place ? db.places.get(leg.arrival_place) : undefined,
          Promise.all(
            members.map(async (member) => ({
              member,
              person: member.person ? await db.persons.get(member.person) : undefined,
            }))
          ),
        ]);
        const entry = entryFromRows({ leg, departurePlace, arrivalPlace, crew });
        // A sealed entry is final; a draft from before the seat existed gets the role defaults.
        return entry.lockedAt ? entry : { ...entry, crew: fillSeats(entry.crew) };
      })
    );

    const registration = aircraft?.registration ?? "";
    return bookView(
      registration,
      headerFromRow(boardBook, this.readHeaderStamp(registration)),
      entries,
      maxStamp(entries)
    );
  }

  /** A consistent read for the page (enter / post-clear), taken under the write
   * lock so it never observes a rewrite-all mid-flight. Internal callers already
   * hold the lock, so they use `load()` directly (serializing here would deadlock). */
  public snapshot(): Promise<BoardBook> {
    return this.serialize(() => this.load());
  }

  /** Rewrite-all persist of the whole book (delete the leg set + re-add one leg per
   * entry). Local-only — the caller pushes the changed row to the kernel.
   *
   * The rewrite runs in one transaction. A rejected leg (a blocking rule such as
   * overlapping legs, or a schema check) or a failed write rolls all of it back, so
   * the stored book stays as it was. The error is rethrown.
   *
   * One failure is not rolled back: logbook-core commits and then writes the store
   * file. When that file write fails, the open database holds the new book although
   * the save is refused. The previous book is then written back, so a later save
   * does not store the refused edit. */
  private async save(view: BoardBook): Promise<void> {
    const db = await this.db();
    const container = this.container;
    if (!container) throw new Error("logbook store is not open");
    const previous = await this.load();
    try {
      await container.transaction(() => this.write(db, view));
    } catch (err) {
      const now = await this.load().catch(() => undefined);
      if (now && JSON.stringify(now) !== JSON.stringify(previous)) {
        await container
          .transaction(() => this.write(db, { ...previous, registration: view.registration }))
          .catch((restoreErr) => console.error("logbook restore after a failed save failed", restoreErr));
      }
      throw err;
    }
  }

  /** Runs inside the transaction of `save()`, so every write passes `IN_TX`.
   * A write without it waits for a transaction of its own and never settles. */
  private async write(db: LogbookDb, view: BoardBook): Promise<void> {
    const aircraftId = await upsert(db.aircraft, { registration: view.registration }, aircraftDto(view.registration));

    const existingBb = (await db.boardBooks.list())[0];
    // date_opened is required: the header value, else the first entry's date, else
    // the stored value, else today (UTC, like every other board-book date).
    const fallbackOpened = view.entries[0]?.date || existingBb?.date_opened || new Date().toISOString().slice(0, 10);
    const bbDto = boardBookDto(aircraftId, view.header, fallbackOpened);
    const bbId = existingBb
      ? (await db.boardBooks.update(existingBb.id, bbDto, IN_TX)).entity.id
      : (await db.boardBooks.add(bbDto, IN_TX)).entity.id;

    for (const leg of await db.legs.list({ board_book_id: bbId })) {
      for (const c of await db.crew.list({ leg_id: leg.id })) {
        await db.crew.delete(c.id, IN_TX);
      }
      await db.legs.delete(leg.id, IN_TX);
    }

    for (const entry of view.entries) {
      // ZZZZ is shared by many distinct places. Match code AND name so one
      // city's label can never leak into another flight's departure/arrival.
      const departure = { ...placeDto(entry.departure, entry.departureName), name: entry.departureName?.trim() || "" };
      const arrival = {
        ...placeDto(entry.destination, entry.destinationName),
        name: entry.destinationName?.trim() || "",
      };
      const departureId = await upsert(db.places, departure, departure);
      const arrivalId = await upsert(db.places, arrival, arrival);
      // The HEMS rule reads the leg's crew, so the mission is set after the crew rows exist.
      const { mission_type, mission_subtype, ...legRow } = legDto(entry, departureId, arrivalId, bbId);
      const legId = (await db.legs.add(legRow, IN_TX)).entity.id;

      // A crew row without a name has no Person to point at.
      for (const member of entry.crew.filter((c) => c.familyName.trim())) {
        const personId = await upsert(db.persons, personFilter(member), personDto(member));
        await db.crew.add(crewDto(member, personId, legId), IN_TX);
      }
      if (mission_type || mission_subtype) await db.legs.update(legId, { mission_type, mission_subtype }, IN_TX);
    }
  }

  /**
   * Create/update ONE entry (a local edit) and push it as its own kernel row
   * (uuid = entry id). Stamps `updatedAt = now` so the edit wins per-entry LWW and
   * assigns a UUID to a fresh entry. Refuses a locked entry. Returns the reloaded
   * book for the page to bind. Applying a peer op never routes here.
   *
   * `loadedUpdatedAt` is the entry's stamp when the form loaded it. When given, the save
   * throws `StaleEntryError` if the stored entry changed or was deleted since, so a peer's
   * edit is never overwritten without the user choosing to. `null` expects no stored entry,
   * so a re-add is refused if a peer added the entry back. The timer flow passes none.
   */
  public async upsertEntry(entry: LogEntry, loadedUpdatedAt?: number | null): Promise<BoardBook> {
    return this.serialize(async () => {
      const book = await this.load();
      const current = book.entries.find((e) => e.id === entry.id);
      if (current?.lockedAt) throw new LockedEntryError(entry.id);
      if (loadedUpdatedAt !== undefined && current?.updatedAt !== (loadedUpdatedAt ?? undefined)) {
        throw new StaleEntryError(entry.id);
      }
      const stamped: LogEntry = { ...entry, lockedAt: "", id: entry.id || generateUuid(), updatedAt: Date.now() };
      return this.commitEntry(book, stamped);
    });
  }

  /**
   * Delete ONE entry and push its tombstone. An exported entry that waits for the Fleetplan
   * sender, or whose state is not known yet, throws `WaitingEntryError`.
   */
  public async deleteEntry(id: string): Promise<BoardBook> {
    return this.serialize(async () => {
      const book = await this.load();
      const current = book.entries.find((e) => e.id === id);
      if (current?.lockedAt) {
        const state = this.lifecycleOf(id);
        if (state === undefined || state === "queued" || state === "syncing") {
          throw new WaitingEntryError(id, state === undefined);
        }
      }
      const updatedAt = Math.max(Date.now(), (current?.updatedAt ?? 0) + 1);
      const entries = book.entries.filter((e) => e.id !== id);
      const next = bookView(this.registrationFor(book), book.header, entries, maxStamp(entries));
      await this.save(next);
      this.pushDelete(id, updatedAt);
      return next;
    });
  }

  /** Save the book header and push it as the `header` kernel row. */
  public async updateHeader(header: BookHeader): Promise<BoardBook> {
    return this.serialize(async () => {
      const book = await this.load();
      const stamped: BookHeader = { ...header, updatedAt: Date.now() };
      const next = bookView(this.registrationFor(book), stamped, book.entries, book.updatedAt);
      await this.save(next);
      this.storeHeaderStamp(next.registration, stamped.updatedAt);
      this.pushUpsert(HEADER_UUID, stamped.updatedAt, headerToBody(stamped));
      return next;
    });
  }

  /**
   * Export ONE entry to Fleetplan (sequence diagram step 18) with one kernel request,
   * which writes the sealed body and queues it. The entry is sealed locally only with
   * the row the kernel answers, so a failed request leaves it editable. A retry of a
   * sealed entry sends the same body, and the kernel answers the same row.
   *
   * The request needs `rw` and `lifecycle` grants on the device token and a reachable
   * kernel host. A 409 `superseded` means the kernel holds a newer body that nobody
   * exported: the entry takes that body, and the pilot checks it and exports again.
   */
  public async queueFleetplanExport(entryId: string): Promise<BoardBook> {
    if (!entryId) throw new Error("entry has no sync id yet");

    return this.serialize(async () => {
      const token = this.clientToken.getTokenSync();
      const scope = this.claims()?.registrationId;
      // Check before the write: a device that cannot queue must not seal the entry.
      if (!token || !scope) throw new Error("not authenticated");
      if (!this.sameRoom()) throw new Error("the session changed during the export");
      const base = kernelHttpBase(this.kernelWsUrl());
      if (!base) throw new Error("kernel endpoint not configured");
      const current = await this.load();
      const entry = current.entries.find((e) => e.id === entryId);
      if (!entry) throw new Error("entry not found");
      // ponytail: a peer edit stamped exactly entry.updatedAt + 1 would still lose; a kernel-side expected-version check is the upgrade.
      const stamp = entry.lockedAt ? entry.updatedAt : entry.updatedAt + 1;
      const sent: LogEntry = entry.lockedAt
        ? entry
        : { ...entry, lockedAt: new Date().toISOString(), updatedAt: stamp };
      // The page checked the entry it showed. A peer edit can land while the confirm is open,
      // so check the body that is sealed: the sender refuses an invalid one and stops the aircraft.
      if (legIssues(entryToBody(sent)).length) throw new SupersededExportError(entryId);
      const url = `${base}/sync/${BOARDBOOK_DATASET}/${encodeURIComponent(scope)}/${encodeURIComponent(entryId)}/export`;
      const res = await this.postKernel(url, token, { updatedAt: stamp, body: entryToBody(sent) });
      if (!this.sameRoom()) throw new Error("the session changed during the export");
      const answer = res.body as {
        updatedAt?: number;
        body?: unknown;
        reason?: string;
        lifecycle?: string | null;
      } | null;
      const ok = res.status >= 200 && res.status < 300;
      const superseded = res.status === 409 && answer?.reason === "superseded";
      if (!ok && !superseded) throw new Error(`export failed (${res.status})`);
      const stored = bodyToEntry(entryId, Number(answer?.updatedAt), answer?.body);
      if (!stored) throw new Error("export answer could not be decoded");
      const entries = replaceOrAppend(current.entries, stored);
      const next = bookView(this.registrationFor(current), current.header, entries, maxStamp(entries));
      await this.save(next);
      if (typeof answer?.lifecycle === "string" || answer?.lifecycle === null) {
        this.lifecycleMap().set(entryId, answer.lifecycle);
      }
      if (superseded) throw new SupersededExportError(entryId);
      if (Number(answer?.updatedAt) !== stamp) throw new SupersededExportError(entryId, true);
      return next;
    });
  }

  /**
   * The block off of the newest leg sent for this aircraft (`null` when none was sent), or
   * `undefined` when the kernel cannot be read. The sender refuses a leg that is not later.
   */
  async sentMark(): Promise<string | null | undefined> {
    const token = this.clientToken.getTokenSync();
    const scope = this.claims()?.registrationId;
    const base = kernelHttpBase(this.kernelWsUrl());
    if (!token || !scope || !base) return undefined;
    const url = `${base}/sync/${BOARDBOOK_DATASET}/${encodeURIComponent(scope)}/mark`;
    try {
      const res = await this.platform.getJson(url, { Authorization: `Bearer ${token}` });
      const mark = (res.body as { mark?: unknown } | null)?.mark;
      if (res.status !== 200) return undefined;
      if (typeof mark === "string") return mark;
      return mark === null ? null : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * POST a JSON body to the kernel and return status and parsed answer.
   *
   * 🔴 The transport is the platform's, never a bare `fetch`. On native a `fetch` to
   * the cloud kernel is refused by CORS, so the export would always fail. See
   * PlatformAdapter.postJson.
   */
  private postKernel(url: string, token: string, body: unknown): Promise<{ status: number; body: unknown }> {
    // "Content-Type" capitalised, as in com.service.ts: CapacitorHttp picks the body
    // encoding off this header, and that spelling is the one proven on native here.
    return this.platform.postJson(url, { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body);
  }

  /**
   * Remove every entry that is not exported, every exported entry that Fleetplan has
   * (`synced`) and every one a peer deleted. An exported entry that waits, failed or has no known state stays: a delete
   * would drop it from the Fleetplan queue. Returns how many exported entries stayed.
   */
  public async clear(): Promise<number> {
    const now = Date.now();
    return this.serialize(async () => {
      const book = await this.load();
      const removable = (e: LogEntry) => !e.lockedAt || ["synced", "deleted"].includes(this.lifecycleOf(e.id) ?? "");
      const kept = book.entries.filter((e) => !removable(e));
      const removed = book.entries.filter(removable);
      await this.save(bookView(this.registrationFor(book), book.header, kept, maxStamp(kept)));
      // Tombstone every removed entry's row so peers don't resurrect them from the kernel.
      for (const e of removed) this.pushDelete(e.id, Math.max(now, e.updatedAt + 1));
      return kept.length;
    });
  }

  /**
   * Opens the kernel room and signals local-DB changes from folded peer ops. The
   * page subscribes and reloads on each emission. The fold itself runs in the
   * service (constructor subscription), independent of the page lifecycle.
   */
  public changes(): Observable<BoardBook> {
    this.ensureSync();
    return this.changed$.asObservable();
  }

  /** Persist one stamped entry into the book and push it. Caller holds the lock. */
  private async commitEntry(book: BoardBook, entry: LogEntry): Promise<BoardBook> {
    const entries = replaceOrAppend(book.entries, entry);
    const next = bookView(this.registrationFor(book), book.header, entries, maxStamp(entries));
    await this.save(next);
    this.pushUpsert(entry.id, entry.updatedAt, entryToBody(entry));
    return next;
  }

  /** Fold one peer row-op into the local DB by per-entry LWW (no re-push — the
   * kernel already broadcasts each row to every peer). */
  private async handleRemoteOp(op: RemoteRowOp): Promise<void> {
    if (op.uuid === LEGACY_BOOK_UUID) return this.migrateLegacyBlob(op);
    if (op.uuid === HEADER_UUID) return this.foldHeader(op);

    const entryOp = toEntryOp(op);
    if (!entryOp) return;
    const stateChanged = this.noteLifecycle(op);
    // The fold keeps a sealed entry that a peer deleted, so it must stay deletable here.
    if (entryOp.kind === "delete") this.lifecycleMap().set(op.uuid, "deleted");
    // A replay from 0 after a restart sends the old row while our delete still waits in the outbox.
    const held = this.outbox.ops(this.storeKey())[op.uuid];
    if (entryOp.kind === "upsert" && held?.type === "delete" && held.updatedAt > op.updatedAt) return;
    const book = await this.load();
    const { entries, changed } = applyEntryOp(book.entries, entryOp);
    if (!changed) {
      // The kernel holds an older unlocked row than the local one, so the local push was lost.
      const local = book.entries.find((e) => e.id === op.uuid);
      if (entryOp.kind === "upsert" && local && !local.lockedAt && local.updatedAt > entryOp.entry.updatedAt) {
        this.pushUpsert(local.id, local.updatedAt, entryToBody(local));
      }
      // The body is the same, but the page shows the new Fleetplan state.
      if (stateChanged) this.changed$.next(book);
      return;
    }
    // Persist under the JWT tail (the sync scope), not the possibly-empty
    // book.registration — a fresh device would else upsert a blank-registration
    // aircraft that a later local edit then duplicates under the real tail.
    const next = bookView(this.registrationFor(book), book.header, entries, maxStamp(entries));
    await this.save(next);
    this.changed$.next(next);
  }

  private async foldHeader(op: RemoteRowOp): Promise<void> {
    if (op.type !== "upsert") return; // the header row is never deleted
    const remote = bodyToHeader(op.updatedAt, op.body);
    if (!remote) return;
    const book = await this.load();
    const registration = this.registrationFor(book);
    // A stored header of another tail (a web login switched tails) is older than any remote one.
    const local = book.registration === registration ? book.header : { ...book.header, updatedAt: 0 };
    const { header, changed } = applyHeaderOp(local, remote);
    if (!changed) {
      // The kernel holds an older header than the stored one, so the local push was lost
      // (the outbound queue is in memory only). A book with no row has nothing to send.
      if (local.dateOpened && local.updatedAt > remote.updatedAt) {
        this.pushUpsert(HEADER_UUID, local.updatedAt, headerToBody(local));
      }
      return;
    }
    const next = bookView(registration, header, book.entries, book.updatedAt);
    await this.save(next);
    this.storeHeaderStamp(registration, header.updatedAt);
    this.changed$.next(next);
  }

  /**
   * A device upgraded from the whole-book format still receives the retained
   * legacy blob (uuid = LEGACY_BOOK_UUID) on subscribe. Explode it into per-entry
   * rows and fold any this device lacks; re-push those exploded entries under their
   * own uuid so they become first-class (Fleetplan-queueable) rows. The legacy blob
   * is left in place (not tombstoned) so a still-legacy peer isn't cleared during a
   * mixed-version window. Deterministic ids keep the explosion idempotent.
   */
  private async migrateLegacyBlob(op: RemoteRowOp): Promise<void> {
    if (op.type !== "upsert") return; // a legacy tombstone: nothing to explode
    const legacy = legacyBlobToEntries(op.body);
    if (!legacy.length) return;

    const book = await this.load();
    let entries = book.entries;
    const toPromote: LogEntry[] = [];
    for (const e of legacy) {
      const r = applyEntryOp(entries, { kind: "upsert", entry: e });
      if (r.changed) {
        entries = r.entries;
        toPromote.push(e);
      }
    }
    if (!toPromote.length) return;

    const next = bookView(this.registrationFor(book), book.header, entries, maxStamp(entries));
    await this.save(next);
    for (const e of toPromote) this.pushUpsert(e.id, e.updatedAt, entryToBody(e));
    this.changed$.next(next);
  }

  /** The book's registration, preferring the JWT tail (the sync scope) over the
   * persisted value so it can't drift from the room this device syncs. */
  private registrationFor(book: BoardBook): string {
    return this.claims()?.registrationId ?? book.registration;
  }

  /**
   * Open (idempotently) the boardbook kernel room for this device. No-op until the
   * endpoint, the device token, and the registration are all known. Tears the
   * socket down on a missing/expired token (the kernel verifies at the WS upgrade).
   */
  public ensureSync(): void {
    const token = this.clientToken.getTokenSync();
    const scope = this.clientToken.getClaims()?.registrationId;
    if (!token || this.clientToken.isTokenExpired(token)) {
      this.kernel.disconnect();
      return;
    }
    const target = boardbookSyncTarget({
      url: this.kernelWsUrl(),
      token,
      scope,
      tenant: this.clientToken.getClaims()?.tenant,
    });
    if (target) this.kernel.connect(target);
    else this.kernel.disconnect();
  }

  /**
   * The kernel `ws(s)://` URL for the CURRENTLY SELECTED backend, as in the
   * capacitor adapter's syncWebSocketUrl(). One build reaches two kernels (ARA's
   * `local` is a LAN address, its `dev` is the cloud host), so a single build-time
   * constant would point both at one.
   *
   * 🔴 Every reader of the kernel host goes through here -- the WS room AND the
   * lifecycle POST. Reading `environment.boardbookKernelWsUrl` directly breaks the
   * deployed SPA: `environment.browser.ts` gates that constant to local hosts and
   * yields "" on a public host, so the Fleetplan export threw "kernel endpoint not
   * configured" while sync itself worked (measured on ara-efb.dev 2026-09-17).
   */
  private kernelWsUrl(): string {
    return this.env.apiInfoNow?.boardbookKernelWsUrl ?? environment.boardbookKernelWsUrl;
  }
}

/** Translate a transport row-op into a fold op; null when an upsert body can't be
 * decoded (unusable row). Envelope carries the authoritative id + updatedAt. */
function toEntryOp(op: RemoteRowOp): EntryOp | null {
  if (op.type === "delete") return { kind: "delete", id: op.uuid, updatedAt: op.updatedAt };
  const entry = bodyToEntry(op.uuid, op.updatedAt, op.body);
  return entry ? { kind: "upsert", entry } : null;
}

/** Replace the entry with the same id, or append it when new. */
function replaceOrAppend(entries: LogEntry[], entry: LogEntry): LogEntry[] {
  return entries.some((e) => e.id === entry.id)
    ? entries.map((e) => (e.id === entry.id ? entry : e))
    : [...entries, entry];
}

/** Book-level stamp = the newest per-entry stamp (display only; sync is per-entry). */
function maxStamp(entries: LogEntry[]): number {
  return entries.reduce((m, e) => Math.max(m, e.updatedAt), 0);
}

/** Kernel HTTP origin derived from its ws(s):// URL (strip the `/ws/sync` path).
 * Empty when the ws URL is unset (prod, until the kernel host is wired). */
function kernelHttpBase(wsUrl: string): string {
  if (!wsUrl) return "";
  return wsUrl.replace(/^ws/, "http").replace(/\/ws\/sync\/?$/, "");
}

/** Find a row matching `filter`; create it from `dto` when absent. Returns its id. */
/** Every write of `LogbookService.write` joins the transaction of `save()`. */
const IN_TX = { inTransaction: true } as const;

/** Find-or-create inside the transaction of `LogbookService.save`. */
async function upsert<T extends { id: string }>(
  svc: EntityService<T>,
  filter: Record<string, unknown>,
  dto: CreateEntityDto<T>
): Promise<string> {
  const existing = (await svc.list(filter))[0];
  return existing ? existing.id : (await svc.add(dto, IN_TX)).entity.id;
}
